/**
 * SINGLE SOURCE OF TRUTH for friendly error copy across the entire app.
 *
 * Every error surface (full-page, toast, modal banner, inline component)
 * pulls from this map so users see consistent language whether the same
 * 404 happens on Orders, Inspections, or Customers.
 *
 * Each entry has:
 *   label   — short uppercase pill text shown above the title (e.g. "NOT FOUND")
 *   title   — the headline (full-page only; toasts skip this)
 *   message — the body explanation; should be one sentence, friendly, actionable
 *
 * Keep messages short. The user already knows what they were trying to do —
 * they don't need a paragraph about it. They need to know what to try next.
 */

export const ERROR_COPY = {
  400: {
    label: "Bad Link",
    title: "Order not found",
    message:
      "That link looks bent — the ID isn't quite right. It may have been mistyped.",
  },
  401: {
    label: "Signed Out",
    title: "You're signed out",
    message: "Your session expired. Sign back in to continue.",
  },
  403: {
    label: "Locked",
    title: "Access denied",
    message:
      "Your account doesn't have permission for this. Ask an admin to grant access.",
  },
  404: {
    label: "Not Found",
    title: "Not found",
    message:
      "We couldn't find what you were looking for. It may have been deleted.",
  },
  408: {
    label: "Slow",
    title: "Took too long",
    message: "The request timed out — probably a slow connection.",
  },
  409: {
    label: "Conflict",
    title: "Out of date",
    message:
      "Someone else updated this while you were editing. Refresh to see their changes.",
  },
  413: {
    label: "Too Big",
    title: "File too large",
    message: "Try a smaller file (under 10MB).",
  },
  422: {
    label: "Won't Fit",
    title: "Can't process this",
    message: "The data you submitted isn't valid. Check the highlighted fields.",
  },
  429: {
    label: "Whoa",
    title: "Slow down",
    message: "Too many requests in a short window. Give it a moment and retry.",
  },
  500: {
    label: "Server Hiccup",
    title: "Something blew a fuse",
    message: "Our server tripped on this one. Try again in a moment.",
  },
  502: {
    label: "Bad Gateway",
    title: "Connection issue",
    message: "The server got an invalid response. Refresh to retry.",
  },
  503: {
    label: "Down for Maintenance",
    title: "Briefly offline",
    message: "We're doing some maintenance. Try again in a minute.",
  },
  504: {
    label: "Slow",
    title: "Took too long",
    message: "The server didn't respond in time. Try again.",
  },
  // String codes for non-HTTP scenarios
  crash: {
    label: "Crashed",
    title: "The page hit a snag",
    message: "Your data is safe — just refresh to recover.",
  },
  offline: {
    label: "Offline",
    title: "You're offline",
    message: "We can't reach the server. Check your connection.",
  },
  default: {
    label: "Error",
    title: "Something went wrong",
    message: "An unexpected error happened. Try refreshing.",
  },
};

/**
 * Look up error copy by code, falling back to the default entry.
 * Returns a guaranteed { label, title, message } object — callers can rely on
 * all three fields being defined.
 */
export function getErrorCopy(code) {
  return ERROR_COPY[code] || ERROR_COPY.default;
}
